/**
 * Цвета графиков — из тем каркаса, а не из своей палитры.
 *
 * **Почему не просто `var(--color-accent)` в свойствах Recharts.** Цвета
 * там уезжают в SVG-атрибуты `stroke` и `fill`, а XML-атрибут `var(...)`
 * не разворачивает: браузер получает буквальную строку и рисует чёрным.
 * Значения приходится получать вычисленными — отсюда этот файл.
 *
 * **Пересчитываются при смене темы.** Дашборд переключают тумблером в
 * шапке, без перезагрузки страницы; график, покрасившийся один раз при
 * монтировании, остался бы тёмным на светлом фоне. Поэтому хук зависит
 * от темы каркаса — той же, которой подчиняется всё остальное.
 *
 * **Своей палитры для рядов нет.** Категорий на графике бывает восемь, а
 * фирменных цветов у ресторана два, и выдумывать шесть недостающих —
 * значит завести вторую дизайн-систему рядом с платформенной. Ряды
 * различаются оттенками фирменного цвета: они заведомо сочетаются между
 * собой и одинаково читаются в обеих темах.
 */

import { useEffect, useState } from 'react';
import { useTheme } from '@shell/theme';

export interface Palette {
  /** Основная линия: выручка, главный ряд. */
  accent: string;
  /** Вспомогательная линия: скользящее среднее, прошлый период. */
  muted: string;
  /** Сетка и оси. */
  grid: string;
  /** Подписи осей и легенды. */
  label: string;
  /** Поверхность подсказки под курсором. */
  surface: string;
  border: string;
  /** Ряд оттенков для разрезов: категории, способы оплаты, официанты. */
  series: string[];
}

/** Что взять, если стилей ещё нет (первый кадр, jsdom в тестах). */
const FALLBACK: Palette = {
  accent: '#7a1f2b',
  muted: '#8b939b',
  grid: '#dfe3e9',
  label: '#61686f',
  surface: '#ffffff',
  border: '#dfe3e9',
  series: ['#7a1f2b', '#a4595f', '#c08b8f', '#6b7280', '#9aa1a9', '#1e6b3a', '#8a5a00'],
};

function readToken(styles: CSSStyleDeclaration, name: string, fallback: string): string {
  const value = styles.getPropertyValue(name).trim();
  return value || fallback;
}

function resolve(): Palette {
  if (typeof window === 'undefined' || !document.documentElement) {
    return FALLBACK;
  }
  const styles = window.getComputedStyle(document.documentElement);
  const accent = readToken(styles, '--color-accent', FALLBACK.accent);
  const muted = readToken(styles, '--color-text-faint', FALLBACK.muted);
  const success = readToken(styles, '--color-success', FALLBACK.series[5]);
  const warning = readToken(styles, '--color-warning', FALLBACK.series[6]);

  return {
    accent,
    muted,
    grid: readToken(styles, '--color-border', FALLBACK.grid),
    label: readToken(styles, '--color-text-muted', FALLBACK.label),
    surface: readToken(styles, '--color-surface', FALLBACK.surface),
    border: readToken(styles, '--color-border', FALLBACK.border),
    // Первым — фирменный: главный ряд на любом графике должен быть узнаваем.
    // Дальше — то, что отличается от него и друг от друга в обеих темах.
    series: [accent, success, warning, muted, FALLBACK.series[1], FALLBACK.series[2]],
  };
}

export function usePalette(): Palette {
  const [theme] = useTheme();
  const [palette, setPalette] = useState<Palette>(resolve);

  useEffect(() => {
    // `dataset.theme` проставляет сам `useTheme` в своём эффекте, и он же
    // может отработать позже нашего. Поэтому чтение откладывается на
    // следующий кадр — иначе первый переключатель темы вернул бы старые
    // цвета, а второй правильные, и выглядело бы это как «через раз».
    const id = requestAnimationFrame(() => setPalette(resolve()));
    return () => cancelAnimationFrame(id);
  }, [theme]);

  return palette;
}
